import {
	Button,
	DatePicker,
	Description,
	Drawer,
	FilterExpanded,
	Form,
	Input,
	Page,
	Select,
	Table,
	Widget,
	Zone
} from '@gimlite/ui-v1'
import { useEffect, useMemo, useState } from 'react'

import {
	baseFormattedDate,
	baseFormattedDuration,
	formatAmount,
	useLazyQuery,
	useMyUrl,
	useTranslation
} from '@gimlite/common'
import {
	Pagination,
	Query,
	QueryCsvTransactionsArgs,
	QueryTransactionsArgs,
	Transaction
} from '../../../../client/graphql'
import { csvTransactionsGql } from '../gql/csvTransactions.gql'
import { transactionsGql } from '../gql/transactions.gql'

// Point of sale
// ServerErr_POS_UserCancelled    ServerError = "POS.UserCancelled"    // user cancelled
// ServerErr_POS_InternalError    ServerError = "POS.InternalError"    // generic internal error
// ServerErr_POS_ConditionsNotMet ServerError = "POS.ConditionsNotMet" // one or many conditions are not met
// ServerErr_POS_FailedToPrint    ServerError = "POS.FailedToPrint"    // failed to print a ticket/receipt
// ServerErr_POS_FailedToUpload   ServerError = "POS.FailedToUpload"   // failed to upload a ticket/report/information...

// // Payment errors
// ServerErr_PAY_Failed ServerError = "PAY.Failed" // A payment operation (completion/refund) failed
const errorMapper: Record<string, string> = {
	'POS.UserCancelled': "Annulé par l'utilisateur",
	'POS.InternalError': 'Erreur interne',
	'POS.ConditionsNotMet': 'Conditions non remplies',
	'POS.FailedToPrint': 'Impression impossible',
	'POS.FailedToUpload': 'Transmission impossible',
	'PAY.Failed': 'Paiement impossible',
	'POS.UserInactivity': 'Time-out usager'
}

const stateTransactionMapper: Record<string, string> = {
	DONE: 'Aboutie',
	ERROR: 'Non aboutie'
}

type RenderedTransaction = Transaction & {
	lpn: string | null
	pushedDated: string | null
	address: string | null
	name: string | null
	gestion: number
	paymentType: string | null
	error: string | null
	payingDuration: number
	totalDuration: number
	state: string
	paymentRecord?: string
}

export const TransactionsPage = () => {
	const { lang } = useTranslation()
	const { getParamsUrl, setParamsUrl } = useMyUrl()
	const [openDrawerId, setOpenDrawerId] = useState<null | string>(null)

	const [fetchTransactions, { loading, error, data, refetch }] = useLazyQuery<
		{ transactions: Query['transactions'] },
		QueryTransactionsArgs
	>(transactionsGql, {
		pollInterval: 1000 * 60
	})

	const [fetchCsvTransactions, { data: csvData }] = useLazyQuery<
		{ csvTransactions: Query['csvTransactions'] },
		QueryCsvTransactionsArgs
	>(csvTransactionsGql, {
		defaultOptions: {},
		notification: {
			success: 'Fichier CSV généré',
			error: 'Erreur lors de la génération du fichier CSV'
		}
	})

	const transactions = useMemo(():
		| {
				list: RenderedTransaction[]
				paging: Pagination
		  }
		| undefined => {
		if (!data?.transactions) return undefined

		const { list, paging } = data?.transactions

		return {
			list: list.map(item => {
				let payingDuration = item.durationDetails?.p || 0
				let totalDuration =
					(item.durationDetails?.p || 0) +
					(item.durationDetails?.np || 0) +
					(item.durationDetails?.f || 0)

				const pushedDated =
					item?.events && Array.isArray(item.events) && item.events.length > 0
						? [...item.events]
								.reverse()
								.find(item => item?.tag && item.tag === 'PUSH')?.createdAt
						: null

				const paymentRecordSort =
					item?.paymentRecordsEntity &&
					Array.isArray(item.paymentRecordsEntity) &&
					item.paymentRecordsEntity.length > 0
						? item.paymentRecordsEntity
								?.filter(Boolean)
								?.sort(
									(b, a) =>
										(a?.paymentDate ? new Date(a.paymentDate).getTime() : 0) -
										(b?.paymentDate ? new Date(b.paymentDate).getTime() : 0)
								)
						: null

				return {
					...item,
					pushedDated:
						!pushedDated || isNaN(new Date(pushedDated).getTime())
							? null
							: pushedDated,
					address: !item.site ? 'web' : item.site?.address1 || null,
					name: item.site?.name || item.consumer || null,
					gestion: 0,
					paymentType:
						paymentRecordSort && paymentRecordSort.length
							? paymentRecordSort[0]?.type || null
							: item?.paymentMethod || null,
					lpn: item.vehicle?.lpn || null,
					error: item.error
						? (errorMapper?.[item.error?.code || ''] as string | undefined) ||
							'Erreur inconnue'
						: null,
					payingDuration,
					totalDuration,
					state:
						(stateTransactionMapper[item.state] as string | undefined) ||
						item.state
				}
			}),
			paging: paging
		}
	}, [data?.transactions])

	useEffect(() => {
		let search = { ...getParamsUrl }

		if (getParamsUrl.time) {
			search = {
				...search,
				beginEnd: getParamsUrl.time
			}
			delete search.time
		}
	}, [getParamsUrl])

	useEffect(() => {
		if (csvData?.csvTransactions.downloadURL) {
			const link = document.createElement('a')
			link.href = csvData.csvTransactions.downloadURL
			const filePath = csvData.csvTransactions.downloadURL.split('/')
			link.setAttribute('download', filePath[filePath.length - 1])
			document.body.appendChild(link)
			link.click()
			document.body.removeChild(link)
		}
	}, [csvData])

	const search = (data: any) => {
		const { amount, lpn, state, time, page, limit } = data

		const searchParams = {
			amount: amount || getParamsUrl?.amount,
			lpn: lpn || getParamsUrl?.lpn,
			state: state || getParamsUrl?.state,
			beginEnd: time || getParamsUrl?.time,
			page: page || getParamsUrl?.page || 1,
			limit: limit || getParamsUrl?.limit || 30
		}

		setParamsUrl(searchParams)
		fetchTransactions({ variables: searchParams })
	}

	useEffect(() => {
		search({})
	}, [])

	const exportCSV = () => {
		fetchCsvTransactions({
			variables: {
				amount: getParamsUrl?.amount * 100,
				lpn: getParamsUrl?.lpn,
				state: getParamsUrl?.state,
				beginEnd: getParamsUrl?.time
			}
		})
	}

	return (
		<Page>
			<Widget
				state={{ loading, error, refetch }}
				config={{
					title: 'Recherche de transactions',
					subtitle: 'Module Transactions'
				}}
			>
				<Zone
					config={{
						zones: [['filter'], ['table']],
						rows: ['min-content', '1fr'],
						columns: ['1fr']
					}}
				>
					<Zone.Area config={{ area: 'filter' }}>
						<FilterExpanded
							data={{
								value: {
									approximatePlale: getParamsUrl?.approximatePlale,
									amount: getParamsUrl?.amount,
									state: getParamsUrl?.state,
									time: getParamsUrl?.time
								}
							}}
							handleEvent={{
								submit: data => search(data)
							}}
						>
							<FilterExpanded.Fields>
								<Form.Item
									config={{
										name: 'lpn',
										label: 'Immatriculation'
									}}
								>
									<Input />
								</Form.Item>
								<Form.Item
									config={{
										name: 'amount',
										label: 'Montant'
									}}
								>
									<Input config={{ type: { name: 'number' } }} />
								</Form.Item>

								<Form.Item
									config={{
										name: 'state',
										label: 'Statut'
									}}
								>
									<Select
										data={{
											items: [
												{
													label: stateTransactionMapper['DONE'],
													value: 'DONE'
												},
												{
													label: stateTransactionMapper['ERROR'],
													value: 'ERROR'
												}
											]
										}}
									/>
								</Form.Item>

								<Form.Item
									config={{
										name: 'time',
										label: 'Date'
									}}
								>
									<DatePicker
										config={{ mode: 'range', format: 'datetime', clear: true }}
									/>
								</Form.Item>
							</FilterExpanded.Fields>

							<FilterExpanded.Actions>
								<Button
									config={{
										text: 'Rechercher',
										type: { value: 'submit' }
									}}
								/>

								<Button
									handleEvent={{
										click: () => {
											setParamsUrl({ page: 1, limit: 30 })
											fetchTransactions({ variables: { page: 1, limit: 30 } })
										}
									}}
									config={{
										text: 'Nettoyer',
										disabled: !(
											getParamsUrl && Object.values(getParamsUrl).length > 0
										)
									}}
								/>

								<Button
									handleEvent={{
										click: exportCSV
									}}
									config={{
										text: 'CSV',
										icon: 'faFileDownloadSolid'
									}}
								/>
							</FilterExpanded.Actions>
						</FilterExpanded>
					</Zone.Area>

					<Zone.Area config={{ area: 'table' }}>
						<>
							{transactions?.list?.map(value => {
								return (
									<Drawer
										key={value._id}
										config={{ isOpen: openDrawerId === value?._id }}
										data={
											<Widget config={{ title: 'Détails' }}>
												<Description
													config={{ mode: 'line' }}
													data={[
														{
															key: 'site',
															label: 'Automate',
															value: value?.name
														},
														{
															key: 'address',
															label: 'Adresse',
															value: value?.address
														},
														{
															key: 'startDate',
															label: 'Début de stationnement',
															value: value.startDate
																? baseFormattedDate({
																		date: value.startDate,
																		lang
																	})
																: null
														},
														{
															key: 'endDate',
															label: 'Fin de stationnement',
															value: value.endDate
																? baseFormattedDate({
																		date: value.endDate,
																		lang
																	})
																: null
														},
														{
															key: 'amount',
															label: 'Montant total',
															value: `${formatAmount({
																amount: parseFloat((value.amount / 100).toFixed(2)),
																currency: value.currency as 'CHF' | 'EUR',
																lang
															})}`
														},
														{
															key: 'Montant total',
															label: 'Immatriculation',
															value: value?.lpn
														},
														{
															key: 'paid_duration',
															label: 'Durée payante',
															value: baseFormattedDuration({
																minutes: value.payingDuration / 60
															})
														},
														{
															key: 'paid_duration',
															label: 'Durée totale',
															value: baseFormattedDuration({
																minutes: value.totalDuration / 60
															})
														},
														{
															key: 'paymentType',
															label: 'Moyen de paiement',
															value: `${value?.paymentType}`
														},
														{
															key: 'tariffCode',
															label: 'Tarif du stationnement',
															value: `${value?.tariffCode}`
														},
														{
															key: 'state',
															label: 'Statut de la transaction',
															value: value?.state
														},
														{
															key: 'error',
															label: 'Raison des non-abouties',
															value: value?.error
														}
													]}
												></Description>
											</Widget>
										}
										handleEvent={{
											close: () => {
												setOpenDrawerId(null)
											}
										}}
									></Drawer>
								)
							})}
							<Table<RenderedTransaction>
								handleEvent={{
									paging: value => search({ ...getParamsUrl, ...value }),
									read: d => setOpenDrawerId(d._id)
								}}
								config={{
									actions: {
										read: true
									},
									pagination: 'click',
									columns: [
										{
											key: 'name',
											title: 'Automate',
											sort: {
												sorter: (a, b) =>
													`${a.name}`.toLowerCase() > `${b.name}`.toLowerCase()
														? -1
														: 1
											}
										},
										{
											key: 'address',
											title: 'Adresse',
											sort: {
												sorter: (a, b) =>
													`${a.address}`.toLowerCase() >
													`${b.address}`.toLowerCase()
														? -1
														: 1
											}
										},
										{
											key: 'startDate',
											title: 'Début de stationnement',
											sort: {
												sorter: (a, b) =>
													(b.startDate ? new Date(b.startDate).getTime() : 0) -
													(a.startDate ? new Date(a.startDate).getTime() : 0),
												defaultSortOrder: 'ascend'
											},
											render: value =>
												value.startDate
													? baseFormattedDate({ date: value.startDate, lang })
													: null
										},
										{
											key: 'endDate',
											title: 'Fin de stationnement',
											sort: {
												sorter: (a, b) =>
													(b.endDate ? new Date(b.endDate).getTime() : 0) -
													(a.endDate ? new Date(a.endDate).getTime() : 0)
											},
											render: value =>
												value.endDate
													? baseFormattedDate({ date: value.endDate, lang })
													: null
										},
										{
											key: 'amount',
											title: 'Montant total',
											sort: {
												sorter: (a, b) => b.amount - a.amount
											},
											render: value =>
												`${formatAmount({
													amount: parseFloat((value.amount / 100).toFixed(2)),
													currency: value.currency as 'CHF' | 'EUR',
													lang
												})}`
										},
										{
											key: 'lpn',
											title: 'Immat.',
											sort: {
												sorter: (a, b) =>
													`${a.lpn}`.toLowerCase() > `${b.lpn}`.toLowerCase()
														? -1
														: 1
											}
										},
										{
											key: 'paymentType',
											title: 'Moyen de paiement',
											render: value =>
												value.paymentType === 'EFT' ? 'CB' : value.paymentType,
											sort: {
												sorter: (a, b) =>
													`${a.paymentType}`.toLowerCase() >
													`${b.paymentType}`.toLowerCase()
														? -1
														: 1
											}
										},
										{
											key: 'tariffCode',
											title: 'Tarif du stationnement',
											sort: {
												sorter: (a, b) =>
													`${a.tariffCode}`.toLowerCase() >
													`${b.tariffCode}`.toLowerCase()
														? -1
														: 1
											}
										},
										{
											key: 'state',
											title: 'Statut de la transaction',
											sort: {
												sorter: (a, b) =>
													`${a.state}`.toLowerCase() >
													`${b.state}`.toLowerCase()
														? -1
														: 1
											}
										},
										{
											key: 'error',
											title: 'Raison des non-abouties',
											sort: {
												sorter: (a, b) =>
													`${a.error}`.toLowerCase() >
													`${b.error}`.toLowerCase()
														? -1
														: 1
											}
										},
										{
											key: 'pushedDated',
											title: 'Envoi sur serveur de tickets',
											sort: {
												sorter: (a, b) => {
													return (
														(b.pushedDated
															? new Date(b.pushedDated).getTime()
															: 0) -
														(a.pushedDated
															? new Date(a.pushedDated).getTime()
															: 0)
													)
												}
											},
											render: value =>
												value.pushedDated
													? baseFormattedDate({ date: value.pushedDated, lang })
													: null
										}
									]
								}}
								data={{
									list: transactions?.list || [],
									paging: transactions?.paging
								}}
							/>
						</>
					</Zone.Area>
				</Zone>
			</Widget>
		</Page>
	)
}
